import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// This component loads site settings and injects tracking codes
export const TrackingScripts = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-settings`);
        setSettings(response.data);
      } catch (err) {
        console.error('Failed to load site settings:', err);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    if (!settings) return;

    // Inject Google Tag Manager (head)
    if (settings.tracking?.gtm_container_id) {
      const gtmScript = document.createElement('script');
      gtmScript.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${settings.tracking.gtm_container_id}');
      `;
      gtmScript.id = 'gtm-script';
      if (!document.getElementById('gtm-script')) {
        document.head.appendChild(gtmScript);
      }

      // GTM noscript (body)
      const gtmNoscript = document.createElement('noscript');
      gtmNoscript.id = 'gtm-noscript';
      gtmNoscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${settings.tracking.gtm_container_id}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      if (!document.getElementById('gtm-noscript') && document.body.firstChild) {
        document.body.insertBefore(gtmNoscript, document.body.firstChild);
      }
    }

    // Inject Google Analytics 4
    if (settings.tracking?.ga4_measurement_id) {
      const ga4Script = document.createElement('script');
      ga4Script.src = `https://www.googletagmanager.com/gtag/js?id=${settings.tracking.ga4_measurement_id}`;
      ga4Script.async = true;
      ga4Script.id = 'ga4-script';
      if (!document.getElementById('ga4-script')) {
        document.head.appendChild(ga4Script);
      }

      const ga4Config = document.createElement('script');
      ga4Config.id = 'ga4-config';
      ga4Config.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${settings.tracking.ga4_measurement_id}');
      `;
      if (!document.getElementById('ga4-config')) {
        document.head.appendChild(ga4Config);
      }
    }

    // Inject Google Ads
    if (settings.tracking?.google_ads_id) {
      const gadsScript = document.createElement('script');
      gadsScript.id = 'gads-script';
      gadsScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('config', '${settings.tracking.google_ads_id}');
      `;
      if (!document.getElementById('gads-script')) {
        document.head.appendChild(gadsScript);
      }

      // Make conversion function available globally
      if (settings.tracking?.google_ads_conversion_id) {
        window.trackConversion = () => {
          if (window.gtag) {
            window.gtag('event', 'conversion', {
              'send_to': `${settings.tracking.google_ads_id}/${settings.tracking.google_ads_conversion_id}`
            });
          }
        };
      }
    }

    // Inject custom head code
    if (settings.custom_code?.head_code) {
      const headContainer = document.createElement('div');
      headContainer.id = 'custom-head-code';
      headContainer.innerHTML = settings.custom_code.head_code;
      if (!document.getElementById('custom-head-code')) {
        // Move scripts to head
        const scripts = headContainer.querySelectorAll('script');
        scripts.forEach(script => {
          const newScript = document.createElement('script');
          if (script.src) {
            newScript.src = script.src;
          } else {
            newScript.innerHTML = script.innerHTML;
          }
          document.head.appendChild(newScript);
        });
        // Move other elements
        const nonScripts = headContainer.querySelectorAll(':not(script)');
        nonScripts.forEach(el => {
          document.head.appendChild(el.cloneNode(true));
        });
      }
    }

    // Inject custom body start code
    if (settings.custom_code?.body_start_code) {
      const bodyStartContainer = document.createElement('div');
      bodyStartContainer.id = 'custom-body-start-code';
      bodyStartContainer.innerHTML = settings.custom_code.body_start_code;
      if (!document.getElementById('custom-body-start-code') && document.body.firstChild) {
        document.body.insertBefore(bodyStartContainer, document.body.firstChild);
      }
    }

    // Inject custom body end code
    if (settings.custom_code?.body_end_code) {
      const bodyEndContainer = document.createElement('div');
      bodyEndContainer.id = 'custom-body-end-code';
      bodyEndContainer.innerHTML = settings.custom_code.body_end_code;
      if (!document.getElementById('custom-body-end-code')) {
        document.body.appendChild(bodyEndContainer);
      }
    }

    // Note: Meta tags are managed by SEOHead component for page-specific SEO
    // TrackingScripts only handles tracking codes and custom code injection

  }, [settings]);

  return null; // This component doesn't render anything
};

// Helper function to track booking conversions (call this after successful booking)
export const trackBookingConversion = () => {
  if (window.trackConversion) {
    window.trackConversion();
  }
  // Also push to dataLayer for GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      'event': 'booking_complete',
      'booking_type': 'taxi_booking'
    });
  }
};

export default TrackingScripts;
